import React from 'react'
import { Routes, Route } from 'react-router-dom'
import { ThemeProvider } from "@/components/theme-provider"
import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"
import { ScrollToTop } from "@/components/ScrollToTop"
import { Canonical } from "@/components/seo/Canonical"
import { LocalBusinessJsonLd } from "@/components/seo/LocalBusiness"
import { GA } from "@/components/seo/GA"
import { RouteLoadingFallback } from "@/components/loading"

const HomePage = React.lazy(() => import("@/pages/HomePage"))
const AboutPage = React.lazy(() => import("@/pages/AboutPage"))
const BlogPage = React.lazy(() => import("@/pages/BlogPage"))
const BlogPostPage = React.lazy(() => import("@/pages/BlogPostPage"))
const BlogCategoryPage = React.lazy(() => import("@/pages/BlogCategoryPage"))
const BlogTagPage = React.lazy(() => import("@/pages/BlogTagPage"))
const NewsletterViewerPage = React.lazy(() => import("@/pages/NewsletterViewerPage"))
const ContactPage = React.lazy(() => import("@/pages/ContactPage"))
const PrivacyPolicyPage = React.lazy(() => import("@/pages/PrivacyPolicyPage"))
const TermsConditionsPage = React.lazy(() => import("@/pages/TermsConditionsPage"))
const AppointmentsPage = React.lazy(() => import("@/pages/AppointmentsPage"))
const NotFoundPage = React.lazy(() => import("@/pages/NotFoundPage"))
const MentoringPage = React.lazy(() => import("@/pages/MentoringPage"))
const WhatWeHelpWithPage = React.lazy(() => import("@/pages/WhatWeHelpWithPage"))
const DrReneeKnottPage = React.lazy(() => import("@/pages/DrReneeKnottPage"))
const ClinicsPage = React.lazy(() => import("@/pages/ClinicsPage"))
const ContinueCarePage = React.lazy(() => import("@/pages/ContinueCarePage"))
const MaroochydoreClinicPage = React.lazy(() => import("@/pages/clinics/maroochydore"))
const NoosaClinicPage = React.lazy(() => import("@/pages/clinics/noosa"))
const FertilityPreconceptionPage = React.lazy(() => import("@/pages/what-we-help-with/fertility-preconception"))
const PregnancyPostpartumPage = React.lazy(() => import("@/pages/what-we-help-with/pregnancy-postpartum"))
const MenstrualHormonalHealthPage = React.lazy(() => import("@/pages/what-we-help-with/menstrual-hormonal-health"))
const StressSleepDigestionPage = React.lazy(() => import("@/pages/what-we-help-with/stress-sleep-digestion"))
const WomensHealthLongTermCarePage = React.lazy(() => import("@/pages/what-we-help-with/womens-health-long-term-care"))
const OtherConcernsPage = React.lazy(() => import("@/pages/what-we-help-with/other-concerns"))
const PackagesTermsPage = React.lazy(() => import("@/pages/PackagesTermsPage"))

// NOTE: Condition pages are now redirected via netlify.toml to /what-we-help-with/* pages
// Files remain in src/pages/conditions/ for potential future use but are not bundled

function App() {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem={false}
      disableTransitionOnChange
    >
      <div className="min-h-screen bg-background">
        <ScrollToTop />
        <Canonical />
        <LocalBusinessJsonLd />
        <GA />
        <Navigation />
        <React.Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/blog/category/:category" element={<BlogCategoryPage />} />
            <Route path="/blog/tag/:tag" element={<BlogTagPage />} />
            <Route path="/newsletters/:slug" element={<NewsletterViewerPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/appointments" element={<AppointmentsPage />} />
            <Route path="/mentoring" element={<MentoringPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsConditionsPage />} />
            <Route path="/packages-terms" element={<PackagesTermsPage />} />
            <Route path="/what-we-help-with" element={<WhatWeHelpWithPage />} />
            <Route path="/dr-renee-knott" element={<DrReneeKnottPage />} />
            <Route path="/clinics" element={<ClinicsPage />} />
            <Route path="/continue-care" element={<ContinueCarePage />} />
            <Route path="/clinics/maroochydore" element={<MaroochydoreClinicPage />} />
            <Route path="/clinics/noosa" element={<NoosaClinicPage />} />
            <Route path="/what-we-help-with/fertility-preconception" element={<FertilityPreconceptionPage />} />
            <Route path="/what-we-help-with/pregnancy-postpartum" element={<PregnancyPostpartumPage />} />
            <Route path="/what-we-help-with/menstrual-hormonal-health" element={<MenstrualHormonalHealthPage />} />
            <Route path="/what-we-help-with/stress-sleep-digestion" element={<StressSleepDigestionPage />} />
            <Route path="/what-we-help-with/womens-health-long-term-care" element={<WomensHealthLongTermCarePage />} />
            <Route path="/what-we-help-with/other-concerns" element={<OtherConcernsPage />} />
            {/* /conditions/* routes are now handled by Netlify 301 redirects in netlify.toml */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Footer />
        </React.Suspense>
      </div>
    </ThemeProvider>
  )
}

export default App